import { useEffect, useState, type MouseEvent } from 'react';

// Where the page is (docs/specs/settings.md §4.7, the owner 2026-11-04). In a browser, which a Tilez
// Hub served, a normal path: / (the server's dashboards), /dash and /dash/<name>, /settings and
// /settings/<part>, /admin and /admin/<dashboard>, /diagnostics. In a shell the same route after the
// # (nobody sees it there), and no / page: the shell opens on the dashboard.

const CHANGED = 'casa-route';

/** A page a Tilez Hub served: no shell announced itself (data/casa_server.ts isServerPage). */
export const isBrowserPage = () : boolean => (globalThis as { casaHost?: unknown }).casaHost === undefined;

/** The page's route: '/', '/dash', '/dash/main-tablet', '/settings/store', '/admin'. */
export function currentRoute() : string {
    if (isBrowserPage()) {
        return location.pathname.replace(/\/+$/, '') || '/';
    }

    const hash = location.hash.replace(/^#\/?/, '');

    // The settings' drivers were a page of their own before (Phase 1).
    if (hash === 'drivers') {
        return '/settings/drivers';
    }

    return hash === '' ? '/dash' : `/${hash}`;
}

/** What a link to `route` points at: the path in a browser, the # in a shell (the dashboard: none). */
export function routeHref(route: string) : string {
    if (isBrowserPage()) {
        return route;
    }

    return route === '/dash' ? location.pathname + location.search : `#${route.slice(1)}`;
}

/** Goes to `route` without loading the page again: a history entry of its own, or in place of this one. */
export function navigate(route: string, options: { replace?: boolean; state?: unknown } = {}) : void {
    const state = options.state ?? null;

    if (options.replace) {
        history.replaceState(state, '', routeHref(route));
    } else {
        history.pushState(state, '', routeHref(route));
    }

    window.dispatchEvent(new Event(CHANGED));
}

/** For a link's onClick: goes there in the page, as a plain click; a click that opens a new tab is left to the browser. */
export function followRoute(event: MouseEvent, route: string) : void {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
    }

    event.preventDefault();
    navigate(route);
}

/** What this history entry keeps: where a page was opened from, and what the settings lie over. */
interface RouteState {
    settingsDepth?: number;
    /** The route the page was opened from, in this tab: its way back goes there (history.back). */
    from?: string;
    /** The settings were opened from the start page: they lie over it, not over a dashboard. */
    overHome?: boolean;
}

export function routeState() : RouteState {
    const state = history.state as RouteState | null;

    return typeof state === 'object' && state !== null ? state : {};
}

/** Opens `route` as a page of its own, remembering where from, so that its way back goes there. */
export function openPage(route: string) : void {
    navigate(route, { state: { from: currentRoute() } });
}

/** For a link's onClick: `openPage`, as a plain click; a click that opens a new tab is left to the browser. */
export function followToPage(event: MouseEvent, route: string) : void {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
    }

    event.preventDefault();
    openPage(route);
}

/** The route, kept up to date as the page goes back and forth. */
export function useRoute() : string {
    const [route, setRoute] = useState(currentRoute);

    useEffect(() => {
        const update = () : void => setRoute(currentRoute());

        window.addEventListener('popstate', update);
        window.addEventListener('hashchange', update);
        window.addEventListener(CHANGED, update);

        return () => {
            window.removeEventListener('popstate', update);
            window.removeEventListener('hashchange', update);
            window.removeEventListener(CHANGED, update);
        };
    }, []);

    return route;
}
